import React, { useState, useEffect } from "react";
import {
  Typography,
  Tag,
  Table,
  AnchorMenu,
  FormError,
  Modal,
  Button,
  TextField,
  Checkbox,
  TableFooter,
} from "components";
import {
  capitalizeFirstLetter,
  formatDateTime,
  formatUrlQuery,
  formatFilterPayload,
} from "utils/helper";
import * as Yup from "yup";
import { Formik, Form, ErrorMessage } from "formik";
import { IoMdClose } from "react-icons/io";
import { ROUTES } from "routes/routePaths";
import makeRequest from "services/makeRequest";
import { routes } from "routes/apiRoutes";
import { useNavigate } from "react-router-dom";
import { isSuccessResponse, setAlert } from "utils/helper";
import successImg from "assets/images/success-image.png";
import rejectedImg from "assets/images/rejected-account-img.png";
import { defaultValidation } from "utils/validators";
import "./style.scss";

const ValidatedKYCs = ({ dataSize }) => {
  const navigate = useNavigate();

  const initialFilterState = {
    entity: 5,
    actionType: 1,
    status: 2,
    number: 1,
    size: dataSize || 10,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const { number, size } = filterState;

  const [data, setData] = useState({
    users: null,
    loading: false,
  });

  const [actionState, setActionState] = useState({
    approvalIds: [],
    actionStatus: null,
  });

  const [selectedIds, setSelectedIds] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "size") {
      filterState.number = 1;
    }
    setSelectedIds([]);
    console.log(selectedIds);
    setFilterState({ ...filterState, [name]: value });
  };

  const getusersData = (queryParams) =>
    makeRequest("get", routes.fetchPendingApprovals(queryParams));

  const loadUsers = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setData({ ...data, loading: true });
    try {
      const res = await getusersData(queryParams);
      setData((prev) => ({
        ...prev,
        users: res.data?.data,
        loading: false,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadUsers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, size]);

  const columns = [
    {
      Header: "S/N",
      Cell: ({ row }) => Number(row.id) + 1,
    },
    {
      Header: "Agent Name",
      Cell: ({ row }) => {
        const { user, corporate, corperateName } = row.original?.details;
        if (corporate) return corperateName;

        return (
          capitalizeFirstLetter(user?.firstName) +
          " " +
          capitalizeFirstLetter(user?.lastName)
        );
      },
    },
    {
      Header: "Corporate",
      Cell: ({ row }) => (row.original?.details.corporate ? "Yes" : "No"),
    },
    {
      Header: "Username",
      Cell: ({ row }) => row.original?.details?.user?.username || "",
    },
    {
      Header: "PM Number",
      Cell: ({ row }) => row.original?.details?.user?.phoneNumber || "",
    },
    // {
    //   Header: "KYC Validated by",
    //   accessor: "",
    // },
    {
      Header: "Date Of Birth",
      Cell: ({ row }) =>
        formatDateTime(row.original?.details?.user?.dateOfBirth),
    },
    {
      Header: "View Details",
      Cell: ({ row }) => (
        <Typography
          variant="caption1"
          className="cursor-pointer"
          onClick={() => {
            navigate(ROUTES.validatedKYC, {
              state: {
                agentId: row.original?.details?.id,
                approvalId: row.original?.approvalId,
              },
            });
          }}
        >
          View Details
          {/* <BsArrowRight /> */}
        </Typography>
      ),
    },
    {
      Header: "Status",
      Cell: () => (
        <Tag
          text={"Pending"}
          backgroundColor={"#FCF6E6"}
          textColor={"#EFB008"}
        />
      ),
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <AnchorMenu
          anchorRender={() => anchorRender(row.original?.approvalId)}
        />
      ),
    },
  ];

  const newColumns = dataSize
    ? columns
    : [
        {
          Header: " ",
          Cell: ({ row }) => (
            <Checkbox
              name={row.id}
              checked={selectedIds.includes(row.original.approvalId)}
              onChange={(e) => {
                let checked = e.target.checked;
                let approvalId = row.original.approvalId;
                if (checked) {
                  setSelectedIds([...selectedIds, approvalId]);
                } else {
                  setSelectedIds(
                    selectedIds.filter(
                      (selectedId) => selectedId !== approvalId
                    )
                  );
                }
              }}
            />
          ),
        },
        ...columns,
      ];

  const anchorRender = (approvalId) => {
    return (
      <ul className="py-1" style={{ paddingLeft: "1rem" }}>
        <li
          className="cursor-pointer"
          onClick={() => {
            setSelectedIds([]);
            setActionState({
              approvalIds: [approvalId],
              actionStatus: 1,
            });
          }}
        >
          <Typography state="primary" variant="body3">
            Approve Account
          </Typography>
        </li>
        <li
          className="cursor-pointer mt-2"
          onClick={() => {
            setSelectedIds([]);
            setActionState({
              approvalIds: [approvalId],
              actionStatus: 3,
            });
          }}
        >
          <Typography state="primary" variant="body3">
            Reject Account
          </Typography>
        </li>
      </ul>
    );
  };

  const rejectionValidationSchema = Yup.object({
    comment: defaultValidation("Comment"),
  });

  const updateAccStatus = async (values) => {
    if (values.approvalIds.length > 30)
      return setAlert("Maximum of 30 approvals allowed");

    try {
      const newValues = {
        approvalIds: values.approvalIds,
        action: actionState.actionStatus === 1 ? 1 : 0, //1 is approved, 0 is declined
      };

      if (values.comment) {
        newValues.comment = values.comment;
      }

      const { data } = await makeRequest(
        "post",
        routes.giveApproval(),
        newValues
      );
      if (isSuccessResponse(data)) {
        if (actionState.approvalIds?.length) {
          setActionState({
            approvalIds: [],
            actionStatus: actionState.actionStatus + 1,
          });
        }
        setSelectedIds([]);
        loadUsers();
      } else {
        setAlert(data.responseMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getAccountValidateContent = (
    <div className="text-center ">
      <Typography variant="subtitle3" state="primary">
        Confirmation
      </Typography>
      <Typography className="my-4" variant="body1" state="primary">
        Are you sure you want to approve{" "}
        {selectedIds.length > 1 ? "these accounts" : "account"}?
      </Typography>
      <Formik
        initialValues={{ approvalIds: actionState.approvalIds }}
        onSubmit={updateAccStatus}
      >
        {({ isSubmitting }) => (
          <Form noValidate autoComplete="off">
            <div className="d-flex justify-content-center">
              <Button
                size="xs"
                label="Cancel"
                variant="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              />
              <Button
                size="xs"
                label="Approve"
                style={{ marginLeft: "1rem" }}
                loading={isSubmitting}
                type="submit"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );

  const getAccountValidateSuccess = (
    <div className="text-center mt-2 mx-auto" style={{ width: "90%" }}>
      <img src={successImg} alt="" width="120" />
      <div style={{ marginTop: "2rem" }}>
        <Typography variant="body1" state="primary" className="mt-3 mb-4">
          Account approval successful.
        </Typography>
      </div>
    </div>
  );

  const getAccountRejectContent = (
    <>
      <Typography variant="body0" className="text-center mb-4" state="primary">
        Are you sure you want to reject{" "}
        {selectedIds.length > 1 ? "these accounts" : "account"}?
      </Typography>
      <Formik
        initialValues={{ approvalIds: actionState.approvalIds, comment: "" }}
        onSubmit={updateAccStatus}
        validationSchema={rejectionValidationSchema}
      >
        {({ isSubmitting, getFieldProps }) => (
          <Form noValidate autoComplete="off">
            <TextField
              name="comment"
              type="text"
              placeholder="Input comment"
              label="Comment"
              containerClassName="mt-2"
              rows="5"
              {...getFieldProps("comment")}
            />
            <ErrorMessage name="comment" component={FormError} />
            <div className="d-flex justify-content-center mt-4">
              <Button
                size="xs"
                label="Cancel"
                variant="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              />
              <Button
                size="xs"
                label="Decline"
                style={{ marginLeft: "1rem" }}
                loading={isSubmitting}
                type="submit"
                variant="danger"
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );

  const getAccountRejectSuccess = (
    <div className="text-center">
      <img src={rejectedImg} alt="Rejected" />
      <Typography variant="body1" state="primary" className="mt-3 mb-4">
        Account rejection successful
      </Typography>
    </div>
  );

  const actionStateIndexes = [
    {
      index: 1,
      content: getAccountValidateContent,
    },
    {
      index: 2,
      content: getAccountValidateSuccess,
    },
    {
      index: 3,
      content: getAccountRejectContent,
    },
    {
      index: 4,
      content: getAccountRejectSuccess,
    },
  ];

  const onClose = () => {
    setActionState({
      approvalIds: [],
      actionStatus: null,
    });
  };

  return (
    <div className="dashboard-page-container">
      <div className="approval-table-container">
        <div className="d-flex justify-content-between align-items-center">
          <Typography
            state="primary"
            component="h4"
            variant="subtitle3"
            className={"mb-2"}
          >
            {dataSize
              ? "New Accounts Awaiting Approval"
              : "Agent Accounts Pending Approval"}
          </Typography>
          {dataSize && (
            <div className="d-flex justify-content-end align-items-center mb-2">
              <Typography
                state="primary"
                variant="caption0"
                className={"cursor-pointer"}
                onClick={() => navigate(ROUTES.validatedKYCs)}
              >
                View All
              </Typography>{" "}
            </div>
          )}
        </div>

        {data.users?.content?.length > 0 && !dataSize && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="pt-2">
              <Checkbox
                readOnly
                name=""
                checked={selectedIds.length === data.users.content.length}
                onClick={(e) => {
                  e.target.checked
                    ? setSelectedIds(
                        data.users?.content?.map((user) => user.approvalId)
                      )
                    : setSelectedIds([]);
                }}
              />{" "}
              &nbsp; Mark all
            </div>
            <div className="d-flex">
              <Button
                size="xs"
                label="Approve all"
                variant="secondary"
                disabled={!selectedIds.length}
                onClick={() => {
                  setActionState({
                    approvalIds: selectedIds,
                    actionStatus: 1,
                  });
                }}
              />

              <Button
                size="xs"
                label="Reject all"
                variant="danger"
                style={{ marginLeft: "1rem" }}
                disabled={!selectedIds.length}
                onClick={() => {
                  setActionState({
                    approvalIds: selectedIds,
                    actionStatus: 3,
                  });
                }}
              />
            </div>
          </div>
        )}

        <Table
          columns={newColumns}
          data={data.users?.content}
          centerData
          loading={data.loading}
        />
        {data?.users?.content?.length && !dataSize ? (
          <TableFooter
            pageSize={filterState.size}
            handleChange={handleChange}
            availableDataLength={data?.users?.content?.length}
            totalDataLength={data.users?.totalElements}
            handlePageClick={(pageNo) => {
              setSelectedIds([]);
              setFilterState({ ...filterState, number: pageNo });
            }}
          />
        ) : (
          ""
        )}
      </div>
      <Modal showModal={!!actionState.actionStatus} onClose={onClose}>
        <div className="my-4">
          <div className="d-flex justify-content-end">
            <IoMdClose className="cursor-pointer" onClick={onClose} />
          </div>
          <div className="mt-2 mx-auto" style={{ width: "90%" }}>
            {actionStateIndexes[actionState.actionStatus - 1]?.content}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ValidatedKYCs;
